/**
 * Addressbook API Transformers
 * Converts between Strapi DTOs and Domain models
 */

import {
  getLocalizedValue,
  mergeLocalizations,
  mergeLocalizationsStrict,
} from '@ufc/core';
import type { StrapiLocalizedDto } from '@ufc/core';
import type {
  Address,
  AddressType,
  AddressStats,
  AddressesFilter,
  City,
  Region,
  Country,
  Contact,
  Location,
  LocationType,
  Tag,
  Tenant,
  CreateAddressRequest,
} from '../domain/types';
import {
  LOCATION_TYPE_TO_ADDRESS_TYPE,
  ADDRESS_TYPE_TO_LOCATION_TYPE,
} from '../domain/types';

import type {
  LocationDto,
  LocationTypeDto,
  CityDto,
  RegionDto,
  CountryDto,
  TagDto,
  TenantDto,
  ContactDto,
  CreateLocationRequest,
  LocationQueryParams,
} from './api-types';

import { buildLocationQuery } from './api-types';

// ============================================================================
// Country Transformers
// ============================================================================

export function transformCountryDtoToCountry(dto: CountryDto): Country {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    isActive: dto.isActive,
  };
}

// ============================================================================
// City Transformers
// ============================================================================

export function transformCityDtoToCity(
  dto: CityDto,
  language: string = 'en',
): City {
  const name = getLocalizedValue(mergeLocalizations(dto, 'name'), language);
  const stateProvinceValue = dto.stateProvince
    ? getLocalizedValue(mergeLocalizations(dto, 'stateProvince'), language)
    : undefined;
  const regionName = dto.region
    ? getLocalizedValue(mergeLocalizations(dto.region, 'name'), language)
    : undefined;

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name,
    stateProvince: stateProvinceValue,
    regionId: dto.region ? String(dto.region.id) : undefined,
    regionDocumentId: dto.region?.documentId,
    regionName,
    countryId: dto.country ? String(dto.country.id) : undefined,
    isActive: dto.isActive,
  };
}

// ============================================================================
// Region Transformers
// ============================================================================

export function transformRegionDtoToRegion(
  dto: RegionDto,
  language: string = 'en',
): Region {
  const factories = Array.isArray(dto.factories) ? dto.factories : [];
  const name = getLocalizedValue(mergeLocalizations(dto, 'name'), language);

  // Extract factory names, handling both localized (object) and plain string formats
  const factoryNames = factories
    .map((factory) => {
      if (!factory?.name) return null;
      // If name is already a string, use it directly
      if (typeof factory.name === 'string') return factory.name;
      // If name is a localized object, extract the correct language
      if (typeof factory.name === 'object') {
        const localizedName = factory.name as Record<string, string>;
        return (
          localizedName[language] ||
          localizedName['en'] ||
          Object.values(localizedName)[0] ||
          null
        );
      }
      return null;
    })
    .filter((name): name is string => Boolean(name));

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name,
    countryId: dto.country ? String(dto.country.id) : undefined,
    factoryIds: factories
      .map((factory) => (factory?.id != null ? String(factory.id) : null))
      .filter((id): id is string => Boolean(id)),
    factoryNames,
    isActive: dto.isActive,
  };
}

// ============================================================================
// Tag Transformers
// ============================================================================

export function transformTagDtoToTag(dto: TagDto): Tag {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    description: dto.description ?? undefined,
    icon: dto.icon ?? undefined,
    color: dto.color ?? undefined,
    categoryId: dto.category ? String(dto.category.id) : undefined,
    sortOrder: dto.sortOrder,
    isActive: dto.isActive,
  };
}

// ============================================================================
// Tenant Transformers
// ============================================================================

export function transformTenantDtoToTenant(dto: TenantDto): Tenant {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    legalName: dto.legalName ?? undefined,
    taxId: dto.taxId ?? undefined,
    vatNumber: dto.vatNumber ?? undefined,
    registrationNumber: dto.registrationNumber ?? undefined,
    status: dto.status,
    settings: dto.settings ?? undefined,
  };
}

// ============================================================================
// Contact Transformers
// ============================================================================

export function transformContactDtoToContact(dto: ContactDto): Contact {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    name: dto.name,
    title: dto.title ?? undefined,
    email: dto.email ?? undefined,
    phone: dto.phone ?? undefined,
    mobile: dto.mobile ?? undefined,
    fax: dto.fax ?? undefined,
    contactType: dto.contactType,
    preferredLanguage: dto.preferredLanguage ?? undefined,
    notes: dto.notes ?? undefined,
    isPrimary: dto.isPrimary,
    isActive: dto.isActive,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

// ============================================================================
// Location Type Transformers
// ============================================================================

export function transformLocationTypeDtoToLocationType(
  dto: LocationTypeDto,
): LocationType {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    description: dto.description ?? undefined,
    icon: dto.icon ?? undefined,
    color: dto.color ?? undefined,
    sortOrder: dto.sortOrder,
    isActive: dto.isActive,
    requiresParent: dto.requiresParent,
    requiresCoordinates: dto.requiresCoordinates,
    requiresLoadingCapabilities: dto.requiresLoadingCapabilities,
    requiresOperatingHours: dto.requiresOperatingHours,
    requiresPermits: dto.requiresPermits,
    allowedChildTypes: dto.allowedChildTypes ?? undefined,
    allowedOwnerTypes: dto.allowedOwnerTypes ?? undefined,
    hierarchyLevelRestriction: dto.hierarchyLevelRestriction ?? undefined,
  };
}

export function transformLocationTypeDtos(
  dtos: LocationTypeDto[],
): LocationType[] {
  return dtos.map(transformLocationTypeDtoToLocationType);
}

// ============================================================================
// Location Transformers
// ============================================================================

export function transformLocationDtoToLocation(
  dto: LocationDto,
  language: string = 'en',
): Location {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    addressLine1: dto.addressLine1 ?? undefined,
    addressLine2: dto.addressLine2 ?? undefined,
    postalCode: dto.postalCode ?? undefined,
    timezone: dto.timezone ?? undefined,
    latitude: dto.latitude ?? undefined,
    longitude: dto.longitude ?? undefined,
    status: dto.status,
    metadata: dto.metadata ?? undefined,
    // Relations
    city: dto.city ? transformCityDtoToCity(dto.city, language) : undefined,
    country: dto.country
      ? transformCountryDtoToCountry(dto.country)
      : undefined,
    locationType: dto.locationType
      ? transformLocationTypeDtoToLocationType(dto.locationType)
      : undefined,
    tenant: dto.tenant ? transformTenantDtoToTenant(dto.tenant) : undefined,
    tags: dto.tags?.map(transformTagDtoToTag) ?? undefined,
    // Timestamps
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

// ============================================================================
// Address Transformers (UI-friendly format)
// ============================================================================

/**
 * Map LocationType code to AddressType
 */
function getAddressTypeFromLocationTypeCode(code?: string): AddressType {
  if (!code) return 'site';
  const mapped = (LOCATION_TYPE_TO_ADDRESS_TYPE as Record<string, AddressType>)[
    code.toLowerCase()
  ];
  return mapped ?? 'site';
}

/**
 * Extract contact info from metadata or primary contact
 */
function extractContactInfo(dto: LocationDto): {
  contactName?: string;
  contactPhone?: string;
} {
  const metadata = dto.metadata as Record<string, unknown> | undefined;

  // Try metadata first
  if (metadata) {
    const contactName = metadata.contactName as string | undefined;
    const contactPhone = metadata.contactPhone as string | undefined;
    if (contactName || contactPhone) {
      return { contactName, contactPhone };
    }
  }

  return {};
}

/**
 * Extract default/favorite flags from metadata
 */
function extractFlags(dto: LocationDto): {
  isDefault?: boolean;
  isFavorite?: boolean;
} {
  const metadata = dto.metadata as Record<string, unknown> | undefined;
  if (!metadata) return {};

  return {
    isDefault: metadata.isDefault as boolean | undefined,
    isFavorite: metadata.isFavorite as boolean | undefined,
  };
}

/**
 * Build full address string from address lines
 */
function buildAddressString(
  addressLine1?: string | null,
  addressLine2?: string | null,
): string {
  const parts = [addressLine1, addressLine2].filter(Boolean);
  return parts.join(', ') || '';
}

/**
 * Transform LocationDto to Address (UI-friendly format)
 */
export function transformLocationToAddress(
  dto: LocationDto,
  language: string = 'en',
): Address {
  const city = dto.city
    ? transformCityDtoToCity(dto.city, language)
    : undefined;
  const { contactName, contactPhone } = extractContactInfo(dto);
  const { isDefault, isFavorite } = extractFlags(dto);

  const loc = dto as unknown as StrapiLocalizedDto;
  const nameI18n = mergeLocalizationsStrict(loc, 'name');
  const line1I18n = mergeLocalizationsStrict(loc, 'addressLine1');
  const line2I18n = mergeLocalizationsStrict(loc, 'addressLine2');
  const hasLine2 = Boolean(line2I18n.en?.trim() || line2I18n.ar?.trim());

  const nameDisplay = getLocalizedValue(nameI18n, language);
  const line1Display = getLocalizedValue(line1I18n, language);
  const line2Display = hasLine2 ? getLocalizedValue(line2I18n, language) : '';

  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: nameDisplay,
    nameI18n,
    type: getAddressTypeFromLocationTypeCode(dto.locationType?.code),
    // Flattened address
    address: buildAddressString(line1Display, line2Display || undefined),
    addressLine1: line1Display,
    addressLine1I18n: line1I18n,
    addressLine2: line2Display || undefined,
    addressLine2I18n: hasLine2 ? line2I18n : undefined,
    city: city?.name ?? '',
    cityId: city ? city.id : undefined,
    cityDocumentId: city?.documentId,
    cityCode: city?.code,
    region: city?.regionName ?? city?.stateProvince ?? undefined,
    regionId: city?.regionId ?? undefined,
    regionDocumentId: city?.regionDocumentId ?? undefined,
    country: dto.country?.name ?? '',
    countryId: dto.country ? String(dto.country.id) : undefined,
    countryCode: dto.country?.code,
    postalCode: dto.postalCode ?? undefined,
    timezone: dto.timezone ?? undefined,
    // Coordinates
    coordinates:
      dto.latitude != null && dto.longitude != null
        ? { lat: dto.latitude, lng: dto.longitude }
        : undefined,
    // Relations
    locationType: dto.locationType
      ? transformLocationTypeDtoToLocationType(dto.locationType)
      : undefined,
    tags: dto.tags?.map(transformTagDtoToTag),
    tenant: dto.tenant
      ? { name: dto.tenant.name, code: dto.tenant.code }
      : undefined,
    // Metadata
    contactName,
    contactPhone,
    isDefault,
    isFavorite,
    // Status
    status: dto.status,
    isActive: dto.status === 'active',
    // Timestamps
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

export function transformLocationsToAddresses(
  dtos: LocationDto[],
  language: string = 'en',
): Address[] {
  return dtos.map((dto) => transformLocationToAddress(dto, language));
}

// ============================================================================
// Request Transformers (Domain → API)
// ============================================================================

/**
 * Generate a location code from name
 */
function generateLocationCode(name: string): string {
  const timestamp = Date.now().toString(36);
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 20);
  return `${slug}-${timestamp}`;
}

/**
 * Transform CreateAddressRequest to Strapi CreateLocationRequest
 */
export function transformAddressToLocationRequest(
  data: CreateAddressRequest,
  tenantId?: number,
  locationTypeId?: number,
): CreateLocationRequest {
  const metadata: Record<string, unknown> = {
    ...data.metadata,
    contactName: data.contactName,
    contactPhone: data.contactPhone,
    isDefault: data.isDefault,
  };

  return {
    code: data.code || generateLocationCode(data.name.en),
    name: data.name.en,
    addressLine1: data.addressLine1.en,
    addressLine2: data.addressLine2?.en,
    postalCode: data.postalCode,
    timezone: data.timezone,
    latitude: data.coordinates?.lat,
    longitude: data.coordinates?.lng,
    status: 'active',
    metadata,
    locationType: locationTypeId,
    tenant: tenantId,
  };
}

// ============================================================================
// Filter Transformers
// ============================================================================

/**
 * Transform AddressesFilter to Strapi query params
 */
export function transformFilterToStrapiQuery(
  filter: AddressesFilter,
): Record<string, string | boolean> {
  const params: LocationQueryParams = {
    // Avoid conflicting populate shapes; defaults are applied by the API client.
    populate: null,
  };

  // Status filter
  if (filter.status && filter.status !== 'all') {
    params.status = filter.status;
  }

  // Type filter (map to location type code)
  if (filter.locationTypeCode) {
    params.locationTypeCode = filter.locationTypeCode;
  } else if (filter.type && filter.type !== 'all') {
    const locationTypeCode = (
      ADDRESS_TYPE_TO_LOCATION_TYPE as Record<string, string>
    )[filter.type];
    if (locationTypeCode) {
      params.locationTypeCode = locationTypeCode;
    }
  }

  // Search filter
  if (filter.search) {
    params.search = filter.search;
  }

  // Tenant filter: if tenantId specified, filter by that tenant; otherwise only show global locations (no tenant)
  if (filter.tenantId) {
    params.tenantId = parseInt(filter.tenantId, 10);
  } else {
    params.noTenant = true;
  }

  if (filter.cityId) {
    const cityId = parseInt(filter.cityId, 10);
    if (!Number.isNaN(cityId)) {
      params.cityId = cityId;
    }
  }

  if (filter.countryCode) {
    params.countryCode = filter.countryCode;
  }

  return buildLocationQuery(params);
}

/**
 * Build Strapi query string from params
 */
export function buildStrapiQueryString(
  params: Record<string, string | boolean | undefined>,
): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return '';

  const queryString = entries
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    )
    .join('&');

  return `?${queryString}`;
}

// ============================================================================
// Stats Calculators
// ============================================================================

/**
 * Calculate address statistics from addresses
 * Groups by location type code (from API) instead of mapped AddressType
 */
export function calculateAddressStats(addresses: Address[]): AddressStats {
  const byType: Record<string, number> = {};

  let active = 0;
  let inactive = 0;

  for (const address of addresses) {
    // Use location type code from API if available, otherwise fall back to mapped type
    const typeKey = address.locationType?.code.toLowerCase() || address.type;
    byType[typeKey] = (byType[typeKey] || 0) + 1;

    if (address.status === 'active') {
      active++;
    } else {
      inactive++;
    }
  }

  return {
    total: addresses.length,
    active,
    inactive,
    byType,
  };
}
